import { useCallback, useMemo } from 'react';
import { ToolbarConfig } from '@bryntum/gantt';
import { BryntumGantt } from '@bryntum/gantt-react';
import { Column, ExcelCreateRequest, FieldType, Horizontal, NumberFormat, StyledCell, Vertical } from 'models/ExcelJs';
import moment from 'moment-timezone';
import { DealExport, RenewalRecord } from 'models/Renewals';
import { message } from 'antd';
import { useExcelApi } from 'api/excel';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import {
  PURCHASE_CHARACTERISTICS_EIGHT_COLORS,
  PURCHASE_CHARACTERISTICS_VALUE,
  RENEWAL_RECORD_TYPE
} from 'shared/constants';

interface Props {
  ganttRef: React.RefObject<BryntumGantt>;
  tasks: Array<RenewalRecord>;
  clientName: string;
  hideExportButton?: boolean;
  groupView?: 'hybrid' | 'flat' | 'vendor';
}

interface CustomFieldExportColumn {
  key: string;
  fieldKey: string;
  label: string;
}

const CUSTOM_FIELD_COLUMN_PREFIX = 'col-customFields-';

const visibleColumnExportConfig: Record<string, Column> = {
  'col-name': { header: 'Deal name', key: 'Deal_name', width: 50, type: FieldType.String },
  'col-categories': { header: 'Categories', key: 'Categories', width: 30, type: FieldType.String },
  'col-renewal': {
    header: 'Renewal',
    key: 'End_date',
    width: 20,
    type: FieldType.Date,
    alignment: {
      horizontal: Horizontal.center,
      vertical: Vertical.middle,
      wrapText: false
    }
  },
  'col-noticePeriod': { header: 'Notice Period', key: 'Notice_period', width: 18, type: FieldType.String },
  'col-alert': { header: 'Alert', key: 'Alert', width: 12, type: FieldType.String },
  'col-fmvReportLink': { header: 'Vantage Report', key: 'Vantage_report', width: 30, type: FieldType.String },
  'col-primaryContact': { header: 'Client Contact', key: 'Client_contact', width: 25, type: FieldType.String },
  'col-status': { header: 'Submitted', key: 'Submitted', width: 18, type: FieldType.String },
  'col-fye': { header: 'Fiscal Year End', key: 'Fiscal_year_end', width: 18, type: FieldType.String },
  'col-vendorComplexity': {
    header: 'Vendor Complexity',
    key: 'Vendor_complexity',
    width: 22,
    type: FieldType.String,
    alignment: {
      horizontal: Horizontal.center,
      vertical: Vertical.middle,
      wrapText: false
    }
  },
  'col-pricingFlexibility': {
    header: 'Pricing Flexibility',
    key: 'Pricing_flexibility',
    width: 22,
    type: FieldType.String,
    alignment: {
      horizontal: Horizontal.center,
      vertical: Vertical.middle,
      wrapText: false
    }
  },
  'col-dealSizeTrend': {
    header: 'Deal Size Trend',
    key: 'Deal_size_trend',
    width: 22,
    type: FieldType.String,
    alignment: {
      horizontal: Horizontal.center,
      vertical: Vertical.middle,
      wrapText: false
    }
  },
  'col-reseller': { header: 'Reseller', key: 'Reseller', width: 25, type: FieldType.String },
  'col-tags': { header: 'Tags', key: 'Tags', width: 25, type: FieldType.String },
  'col-tcvAgg': { header: 'TCV', key: 'TCV', width: 20, type: FieldType.Number, numFmt: NumberFormat.currency }
};

const getCategoryNames = (node: any): string =>
  (node.contract_category || [])
    .flatMap((category: any) => [
      category.category_root_name,
      category.category_prime_name,
      ...(category.category_second?.map((second: any) => second.category_second_name) || [])
    ])
    .filter(Boolean)
    .join(', ');

const getSupplierDynamicValue = (node: any, key: string): string | number | null => {
  const dynamic = node.supplierDynamics?.find((item: any) => item?.dealDynamic?.key === key);
  return dynamic?.value ?? null;
};

const formatVendorLabel = (raw: string | number | null): string => {
  if (raw === null || raw === undefined || raw === '') return '';
  return PURCHASE_CHARACTERISTICS_VALUE[Number(raw) - 1]?.charAt(0) ?? '';
};

// ARGB colors matching PURCHASE_CHARACTERISTICS_THREE_COLORS used in the calendar UI
const VENDOR_LABEL_FILL: Record<string, string> = {
  L: 'FFA1CD6C', // green  – Low
  M: 'FFFFC106', // yellow – Medium
  H: 'FFEE7267' // red    – High
};

const toStyledVendorLabel = (raw: string | number | null): StyledCell | string => {
  const label = formatVendorLabel(raw);
  if (!label) return '';
  return { __cellFill__: VENDOR_LABEL_FILL[label] ?? '', value: label };
};

const toArgb = (hexColor: string): string => {
  return `FF${hexColor.replace('#', '').toUpperCase()}`;
};

const getDealSizeTrendColor = (percentageValue: number): string => {
  let index;
  if (percentageValue <= -20) {
    index = 0;
  } else if (percentageValue >= 20) {
    index = 7;
  } else {
    index = Math.floor((percentageValue + 20) / 5);
  }
  return PURCHASE_CHARACTERISTICS_EIGHT_COLORS[index];
};

const toStyledDealSizeTrend = (raw: string | number | null): StyledCell | string => {
  if (raw === null || raw === undefined || raw === '') return '';

  const numericValue = Number(raw);
  if (!Number.isFinite(numericValue)) return '';

  const percentageValue = Math.round(numericValue * 100);
  const fillColor = getDealSizeTrendColor(percentageValue);
  if (!fillColor) return `${percentageValue}%`;

  return {
    __cellFill__: toArgb(fillColor),
    value: `${percentageValue}%`
  };
};

const getCustomFieldValue = (node: any, customFieldColumn: CustomFieldExportColumn): string | number | null => {
  const customField = node.customFields?.find(
    (field: any) => field?.key === customFieldColumn.fieldKey || field?.label === customFieldColumn.label
  );

  if (!customField || customField.value === null || customField.value === undefined || customField.value === '') {
    return null;
  }

  if (customField.data_type === 'boolean') return customField.value ? 'Yes' : 'No';
  if (customField.data_type === 'number') return Number(customField.value);
  return customField.value;
};

const getColumnHeader = (column: any): string =>
  String(column?.text || column?.field || column?.id || 'Column')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getColumnStoreRecords = (columnsStore: any): any[] => {
  if (!columnsStore) return [];
  if (Array.isArray(columnsStore)) return columnsStore;
  if (Array.isArray(columnsStore.records)) return columnsStore.records;
  if (Array.isArray(columnsStore.allRecords)) return columnsStore.allRecords;
  if (typeof columnsStore.toArray === 'function') return columnsStore.toArray();
  return [];
};

const getVisibleLeafColumns = (columns: any[], parentVisible = true): any[] =>
  columns.flatMap((column) => {
    const isVisible =
      parentVisible &&
      column?.hidden !== true &&
      column?.isVisible !== false &&
      (typeof column?.isVisible !== 'function' || column.isVisible() !== false);
    const children = Array.isArray(column?.children) ? column.children : [];

    return children.length > 0 ? getVisibleLeafColumns(children, isVisible) : isVisible ? [column] : [];
  });

const getVisibleExportColumns = (
  columnsStore: any
): { columns: Column[]; customFieldColumns: CustomFieldExportColumn[] } => {
  const columns: Column[] = [];
  const customFieldColumns: CustomFieldExportColumn[] = [];
  const usedKeys = new Set<string>();

  getVisibleLeafColumns(getColumnStoreRecords(columnsStore)).forEach((column) => {
    const columnId = String(column?.id || '');
    const nativeColumn = visibleColumnExportConfig[columnId];

    if (nativeColumn && !usedKeys.has(nativeColumn.key)) {
      columns.push(nativeColumn);
      usedKeys.add(nativeColumn.key);
    } else if (columnId.startsWith(CUSTOM_FIELD_COLUMN_PREFIX)) {
      const label = getColumnHeader(column);
      const baseKey = `Custom_${label.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`;
      let key = baseKey;
      let suffix = 2;
      while (usedKeys.has(key)) {
        key = `${baseKey}_${suffix}`;
        suffix += 1;
      }
      const type =
        column?.type === FieldType.Date || column?.type === FieldType.Number ? column.type : FieldType.String;

      columns.push({
        header: label,
        key,
        width: Math.min(Math.max(label.length + 4, 16), 30),
        type,
        ...(type === FieldType.Date
          ? { alignment: { horizontal: Horizontal.center, vertical: Vertical.middle, wrapText: false } }
          : {})
      });
      customFieldColumns.push({ key, fieldKey: columnId.replace(CUSTOM_FIELD_COLUMN_PREFIX, ''), label });
      usedKeys.add(key);
    }
  });

  return { columns, customFieldColumns };
};

export const RenewalGanttChartToolbarItems = ({ ganttRef, tasks, clientName, hideExportButton, groupView }: Props) => {
  const { createExcel } = useExcelApi();

  /**
   * Recursively traverses a tree of renewal records to extract deal information
   * @param {RenewalRecord[]} tasksTree - Array of renewal records representing the tree structure
   * @param {CustomFieldExportColumn[]} customFieldColumns - Visible custom field columns to append to export rows
   * @returns {DealExport[]} Array of DealExport objects containing flattened deal information
   * @description Extracts deal information from leaf nodes of the tasks tree.
   */
  const getAllDeals = useCallback(
    (
      tasksTree: RenewalRecord[],
      customFieldColumns: CustomFieldExportColumn[] = []
    ): Array<DealExport & Record<string, any>> => {
      return _.flatMapDeep(tasksTree, (node: any) => {
        if (node.recordType === undefined && node.children) {
          return getAllDeals(node.children, customFieldColumns);
        }

        const isLeaf = !node.children || node.children.length === 0;
        const isDeal =
          node.recordType === RENEWAL_RECORD_TYPE.SUPPLIER_DEAL || node.recordType === RENEWAL_RECORD_TYPE.DEAL;

        if (isLeaf && isDeal) {
          const exportRow: DealExport & Record<string, any> = {
            Vendor_name: node.orgName,
            Deal_name: node.orgName ? `${node.orgName}: ${node.subject || node.name || ''}` : node.subject,
            Deal_id: node.status !== 'Calendar' ? node.name : 'NA',
            TCV: node.tcv,
            Start_date: node.startDate,
            End_date: node.endDate,
            Categories: getCategoryNames(node),
            Notice_period: node.notice_period != null ? `${node.notice_period} days` : '',
            Alert: Array.isArray(node.notification_queue) && node.notification_queue.length > 0 ? 'Yes' : 'No',
            Vantage_report: node.fmvReportLink || '',
            Client_contact: node.clientContact?.Name || '',
            Submitted: node.status || '',
            Fiscal_year_end: node.fiscal_year_end ? moment(node.fiscal_year_end, 'MM-DD', true).format('MMM Do') : '',
            Vendor_complexity: toStyledVendorLabel(getSupplierDynamicValue(node, 'vendorComplexity')),
            Pricing_flexibility: toStyledVendorLabel(getSupplierDynamicValue(node, 'pricingFlexibility')),
            Deal_size_trend: toStyledDealSizeTrend(getSupplierDynamicValue(node, 'dealSizeTrend')),
            Reseller: node.reseller?.name || '',
            Tags: Array.isArray(node.tags)
              ? node.tags
                  .map((tag: any) => tag.name)
                  .filter(Boolean)
                  .join(', ')
              : ''
          };

          customFieldColumns.forEach((customFieldColumn) => {
            exportRow[customFieldColumn.key] = getCustomFieldValue(node, customFieldColumn);
          });

          return exportRow;
        }

        if (node.children && node.children.length > 0) {
          return getAllDeals(node.children, customFieldColumns);
        }

        return [];
      });
    },
    []
  );

  const handleExcelExport = useCallback(async () => {
    if (tasks.length > 0) {
      const today = moment().format('YYYY-MM-DD');
      const filename = `${clientName}_${today}.xlsx`;
      const { columns, customFieldColumns } = getVisibleExportColumns(ganttRef.current?.instance?.columns);

      if (columns.length === 0) {
        message.warning({ content: 'No visible columns to export', key: 'excelExport' });
        return;
      }

      const formData: ExcelCreateRequest = {
        data: getAllDeals(tasks, customFieldColumns),
        columns,
        options: { fileName: filename, workSheetName: 'NPI Deals' }
      };

      try {
        message.loading({ content: 'Creating Excel file ...', key: 'excelExport' });
        const excelData = await createExcel(formData);
        fileDownload(excelData, filename);
        message.success({ content: 'Excel Exported Successfully', key: 'excelExport' });
      } catch (e) {
        message.error({ content: 'Problem creating Excel file.', key: 'excelExport' });
      }
    } else {
      message.warning({ content: 'No data to export', key: 'excelExport' });
    }
  }, [clientName, createExcel, ganttRef, getAllDeals, tasks]);

  const items: ToolbarConfig['items'] = useMemo(
    () => [
      {
        type: 'buttonGroup',
        items: [
          {
            ref: 'exportExcelButton',
            icon: 'b-fa b-fa-file-excel',
            text: 'EXPORT',
            tooltip: 'Export View To Excel',
            hidden: hideExportButton,
            onAction: () => handleExcelExport()
          }
        ]
      },
      {
        type: 'buttonGroup',
        items: [
          {
            ref: 'expandAllButton',
            icon: 'b-fa b-fa-angle-double-down',
            tooltip: groupView === 'flat' ? 'Not available in Flat view' : 'Expand all',
            disabled: groupView === 'flat',
            onAction: () => ganttRef?.current?.instance.expandAll()
          },
          {
            ref: 'collapseAllButton',
            icon: 'b-fa b-fa-angle-double-up',
            tooltip: groupView === 'flat' ? 'Not available in Flat view' : 'Collapse all',
            disabled: groupView === 'flat',
            onAction: () => ganttRef?.current?.instance.collapseAll()
          }
        ]
      },
      {
        type: 'buttonGroup',
        items: [
          {
            ref: 'zoomInButton',
            icon: 'b-fa b-fa-search-plus',
            tooltip: 'Zoom in',
            onAction: () => ganttRef?.current?.instance.zoomIn()
          },
          {
            ref: 'zoomOutButton',
            icon: 'b-fa b-fa-search-minus',
            tooltip: 'Zoom out',
            onAction: () => ganttRef?.current?.instance.zoomOut()
          },
          {
            ref: 'zoomToFitButton',
            icon: 'b-fa b-fa-compress-arrows-alt',
            tooltip: 'Zoom to fit',
            onAction: () => ganttRef?.current?.instance.zoomToFit({ leftMargin: 50, rightMargin: 50 })
          },
          {
            ref: 'previousButton',
            icon: 'b-fa b-fa-angle-left',
            tooltip: 'Previous time span',
            onAction: () => ganttRef?.current?.instance.shiftPrevious()
          },
          {
            ref: 'nextButton',
            icon: 'b-fa b-fa-angle-right',
            tooltip: 'Next time span',
            onAction: () => ganttRef?.current?.instance.shiftNext()
          }
        ]
      }
    ],
    [ganttRef, handleExcelExport, groupView, hideExportButton]
  );
  return items;
};
